import { FileIcon, defaultStyles } from 'react-file-icon';

import Avatar from 'react-avatar';
import DOMPurify from 'isomorphic-dompurify';
import { Link, IconButton, Tooltip, Skeleton } from "@mui/material";
import { ListItem } from "@mui/material";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import EditOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import CircularProgress from '@mui/material/CircularProgress';
import React from "react";
import InlineVideo from './InlineVideo';
import { linkify, simple_markdown_to_html, ota_reply_links, shorten_attachment_links, is_all_media_links } from './utils/chat_utils'
import { MESSAGE_TYPE_TOKENS, SAME_SENDER_GAP_PX, DIFFERENT_SENDER_GAP_PX } from './styles/messageTypeTokens'
import { cv } from '../themeVars';
import { sendMethodsLabel } from './sendMethodLabel'

class ChatItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      message: null,
      tw_identity: "",
      mediaUrls: null, // per-media-group temporary URLs, fetched lazily once the message nears the viewport
      loadedMedia: {}, // media-group index -> true once its inline <img> has loaded
      localThumbsUp: null, // null = use message attributes, true/false = override
      localSuggestedEdit: null, // null = use message attributes, true/false = override
      translation: null,        // translated text once fetched
      translationLang: null,    // ISO target language of the translation
      translationSourceLang: null, // ISO detected source language (if backend returns it)
      translationVisible: false,
      translationLoading: false,
      translationError: null,
    };

    this.state.message = this.props.message;
    this.state.tw_identity = this.props.tw_identity;
    this.mediaGroups = this.computeMediaGroups();
    this.mediaContainerRef = React.createRef();
    this.mediaObserver = null;
    this.fetchingMediaUrls = false;
    this.mounted = false;
    this.messageWrapperRef = null;
    //this.boundChannelUpdatedListener = null;
  }

  trimFileName(filename, limit=15, spacer='.') {
    if(!filename){
      return "";
    }
    const split = filename.indexOf(".");
    const name = filename.substring(0, split);
    const ext = filename.substring(split);

    let trimName = name.substring(0, limit);

    if (name.length > trimName.length)
      trimName = trimName.padEnd(limit + 3, spacer);

    return trimName + ext;
  }

  // Group attachments without fetching any URLs. Pairs an image with its
  // same-name video (the image is the video's preview/thumbnail) and keeps
  // CP-7062 ordering.
  computeMediaGroups = () => {
    const message = this.props.message;
    if (message.type !== "media" || !message.attachedMedia) {
      return [];
    }
    let attachedMedia = message.attachedMedia;

    // CP-7062 Reorder attachedMedia if ordered_media_filenames is provided
    const orderedFilenames = message.attributes?.ordered_media_filenames;
    if (Array.isArray(orderedFilenames) && orderedFilenames.length > 0) {
      // Create a map of filename to media item for quick lookup
      const mediaMap = new Map();
      attachedMedia.forEach(media => {
        if (media.filename) {
          mediaMap.set(media.filename, media);
        }
      });

      // Build reordered array: first ordered files, then remaining files
      const reorderedMedia = [];
      const usedFilenames = new Set();

      // Add files in the order specified by ordered_media_filenames
      orderedFilenames.forEach(filename => {
        const media = mediaMap.get(filename);
        if (media) {
          reorderedMedia.push(media);
          usedFilenames.add(filename);
        }
      });

      // Add remaining files that weren't in the ordered list
      attachedMedia.forEach(media => {
        if (media.filename && !usedFilenames.has(media.filename)) {
          reorderedMedia.push(media);
        }
      });

      attachedMedia = reorderedMedia;
    }

    // if the filename includes a period, strip the extension
    const stripExt = (filename) => filename && filename.includes('.')
      ? filename.split('.').slice(0, -1).join('.')
      : filename;

    const groups = [];
    for (let i = 0; i < attachedMedia.length; i++) {
      // Check if we should combine the image and video into one attachment
      // (the image being the preview/thumbnail for the video)
      if (i + 1 < attachedMedia.length && stripExt(attachedMedia[i].filename) === stripExt(attachedMedia[i + 1].filename)) {
        let imgFileIdx;
        let vidFileIdx;
        if (attachedMedia[i].contentType.startsWith("image") && !attachedMedia[i + 1].contentType.startsWith("image")) {
          imgFileIdx = i;
          vidFileIdx = i + 1;
        }
        else if (attachedMedia[i + 1].contentType.startsWith("image") && !attachedMedia[i].contentType.startsWith("image")) {
          imgFileIdx = i + 1;
          vidFileIdx = i;
        }
        else if (attachedMedia[i].contentType.startsWith("image")) {
          imgFileIdx = i;
          vidFileIdx = undefined;
        }
        if (imgFileIdx !== undefined) {
          groups.push({
            kind: "image_video",
            imgMedia: attachedMedia[imgFileIdx],
            vidMedia: vidFileIdx !== undefined ? attachedMedia[vidFileIdx] : undefined,
          });
          // since they were combined, advance i and skip ahead
          i++;
          continue;
        }
      }

      // if not combined, group files individually
      const media = attachedMedia[i];
      if (media.contentType.startsWith("image")) {
        groups.push({ kind: "image", media });
      }
      else if (media.contentType.startsWith("video")) {
        groups.push({ kind: "video", media });
      }
      else {
        groups.push({ kind: "file", media });
      }
    }
    return groups;
  };

  // Twilio temporary media URLs expire after ~5 minutes, so inline URLs are
  // fetched only once the message nears the viewport (see componentDidMount),
  // and clicks always fetch a fresh URL (see openMediaInNewTab) instead of
  // refreshing every inline URL on a timer.
  fetchMediaUrls = async () => {
    if (this.state.mediaUrls || this.fetchingMediaUrls) {
      return;
    }
    this.fetchingMediaUrls = true;
    try {
      const mediaUrls = await Promise.all(this.mediaGroups.map(async (group) => {
        if (group.kind === "image_video") {
          return { imgUrl: await group.imgMedia.getContentTemporaryUrl() };
        }
        if (group.kind === "file") {
          // No inline preview; the URL is fetched on click
          return {};
        }
        return { url: await group.media.getContentTemporaryUrl() };
      }));
      if (this.mounted) {
        this.setState({ mediaUrls });
      }
    } catch (e) {
      console.warn("ChatItem: failed to fetch media URLs:", e);
      this.fetchingMediaUrls = false;
    }
  };

  // The window must be opened synchronously so popup blockers allow it; the
  // URL is filled in once the fresh fetch resolves.
  openMediaInNewTab = (media) => (e) => {
    e.preventDefault();
    if (!media) {
      return;
    }
    const win = window.open("", "_blank");
    media.getContentTemporaryUrl()
      .then((url) => {
        if (win && url) {
          win.location.href = url;
        }
        else if (win) {
          win.close();
        }
      })
      .catch((err) => {
        console.warn("ChatItem: failed to fetch media URL on click:", err);
        if (win) {
          win.close();
        }
      });
  };

  handleMediaLoaded = (idx) => {
    this.setState((prev) => ({ loadedMedia: { ...prev.loadedMedia, [idx]: true } }));
    if (this.props.onMediaLoad) {
      this.props.onMediaLoad();
    }
  };

  renderMediaGroup = (group, idx) => {
    const urls = this.state.mediaUrls ? this.state.mediaUrls[idx] : null;
    const imageLoaded = !!this.state.loadedMedia[idx];

    if (group.kind === "image" || group.kind === "image_video") {
      const inlineUrl = group.kind === "image" ? urls?.url : urls?.imgUrl;
      const clickMedia = group.kind === "image" ? group.media : (group.vidMedia || group.imgMedia);
      return (
        <a key={idx} href={inlineUrl} target="_blank" rel="noreferrer" onClick={this.openMediaInNewTab(clickMedia)} style={{ cursor: "pointer" }}>
          <div>
            <br />
            <div style={styles.videoThumbnailContainer}>
              {!imageLoaded && <Skeleton variant="rectangular" animation="wave" sx={styles.imageSkeleton} />}
              {inlineUrl &&
                <img style={{ ...styles.imageDiv, display: imageLoaded ? undefined : "none" }} src={inlineUrl}
                  onLoad={() => this.handleMediaLoaded(idx)} alt="" width="100%" height="auto" />}
              {group.kind === "image_video" && group.vidMedia && imageLoaded && <div style={styles.playButtonOverlay}>
                <svg width="48" height="48" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="23" fill="rgba(0,0,0,0.5)" stroke="white" strokeWidth="2" />
                  <polygon points="19,14 19,34 36,24" fill="white" />
                </svg>
              </div>}
            </div>
          </div>
        </a>
      );
    }

    if (group.kind === "video") {
      return (
        <React.Fragment key={idx}>
          <br />
          {urls?.url
            ? <InlineVideo src={urls.url} getFreshSrc={() => group.media.getContentTemporaryUrl()} imageStyle={styles.imageDiv} />
            : <Skeleton variant="rectangular" animation="wave" sx={styles.imageSkeleton} />}
        </React.Fragment>
      );
    }

    const ct = group.media.contentType.split('/')[1];
    return (
      <a key={idx} style={{ ...styles.mediaLinkText, cursor: "pointer" }} onClick={this.openMediaInNewTab(group.media)}>
        <br />
        <div style={{ width: "30px", marginLeft: "auto", marginRight: "auto" }}>
          <FileIcon extension={ct} {...defaultStyles[ct]} />
        </div>
        <div style={{ marginLeft: "auto", marginRight: "auto" }}>{this.trimFileName(group.media.filename)}</div>
      </a>
    );
  };

  componentDidMount = () => {
    this.mounted = true;
    if (this.mediaGroups.length === 0) {
      return;
    }
    const el = this.mediaContainerRef.current;
    if (el && typeof IntersectionObserver !== "undefined") {
      // Lazily fetch media URLs (and hence download the images) only once the
      // message is scrolled near the viewport
      this.mediaObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          if (this.mediaObserver) {
            this.mediaObserver.disconnect();
            this.mediaObserver = null;
          }
          this.fetchMediaUrls();
        }
      }, { rootMargin: "600px 0px" });
      this.mediaObserver.observe(el);
    }
    else {
      this.fetchMediaUrls();
    }
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.mediaObserver) {
      this.mediaObserver.disconnect();
      this.mediaObserver = null;
    }
  };

  parseInside = (txt, left, right) => {
    return left + txt + right;
  }

  handleTranslateClick = async () => {
    // If a translation is already in-memory, just toggle its visibility.
    if (this.state.translation != null) {
      this.setState({ translationVisible: !this.state.translationVisible });
      return;
    }
    if (this.state.translationLoading) {
      return;
    }
    if (typeof this.props.translateMessage !== 'function') {
      this.setState({ translationError: "Translation isn't available yet." });
      return;
    }
    this.setState({ translationLoading: true, translationError: null });
    try {
      const res = await this.props.translateMessage(this.state.message);
      if (res && res.translated_text) {
        this.setState({
          translation: res.translated_text,
          translationLang: res.target_lang || null,
          translationSourceLang: res.source_lang || null,
          translationVisible: true,
          translationLoading: false,
          translationError: null,
        });
      } else {
        this.setState({
          translationLoading: false,
          translationError: (res && res.error) || "Couldn't translate this message.",
        });
      }
    } catch (e) {
      console.warn("ChatItem.handleTranslateClick:", e);
      this.setState({
        translationLoading: false,
        translationError: e?.message || "Couldn't translate this message.",
      });
    }
  };


  render() {

    const isOwnMessage = (this.state.message.author === this.state.tw_identity);

    let body = this.state.message.body;

    const send_methods = this.state.message.attributes['send_methods'];

    let source_platform;
    let exclude_delivery_to_agents = this.state.message.attributes['exclude_delivery_to_agents'];
  
    if(send_methods){
      source_platform = sendMethodsLabel(send_methods);
    }
    else{
      source_platform = "in‑app";
      const source_platform_raw = this.state.message.attributes['source_platform']?.toLowerCase();
      // "airbnb-group" (departure-wide Airbnb channel thread) must precede "airbnb" —
      // the match below is a substring test, so the shorter name would swallow it.
      const delivery_methods = ["email", "sms", "whatsapp", "airbnb-group", "airbnb", "automated system message"]

      for (var i = 0; i < delivery_methods.length; i++) {
        if (source_platform_raw?.includes(delivery_methods[i])) {
          source_platform = delivery_methods[i];
          if(source_platform === 'automated system message'){
            source_platform = 'in-app'
          }
          break;
        }
      }
    }

    // Check if this is an AI AUTO-REPLY message
    const source_platform_attr = this.state.message.attributes['source_platform'];
    const isAIAutoReply = source_platform_attr && source_platform_attr.toUpperCase().includes('AI AUTO-REPLY');

    // Decision green-lit by Ben (Google Chat discussion, 8/17/2026): <a href> tags in message
    // bodies render as real links ONLY for admin-authored or automated system messages (operator-controlled
    // content — pasted email templates like Paxflow pickup notices legitimately carry anchors).
    // Guest/guide/observer messages keep the CP-8139 escape, so an untrusted sender can't hide a
    // phishing href behind innocuous link text. Automated detection: notification relays carry
    // send_methods; system auto-replies stamp "AUTOMATED SYSTEM MESSAGE" into source_platform.
    // Until participant roles load, the role lookup misses and links stay escaped (re-renders once
    // roles arrive).
    const allow_links = this.props.participantsRoleDict?.[this.state.message.author] === 'Admin'
      || !!send_methods
      || !!(source_platform_attr && source_platform_attr.toUpperCase().includes('AUTOMATED SYSTEM MESSAGE'));

    // Check if current user has given thumbs up feedback
    // Use local state if set, otherwise check message attributes
    let hasUserThumbsUp = false;
    let hasUserSuggestedEdit = false;
    if (isAIAutoReply) {
      if (this.state.localThumbsUp !== null) {
        hasUserThumbsUp = this.state.localThumbsUp;
      } else {
        try {
          const currentUserId = JSON.parse(window.localStorage.getItem('bootstrap'))?.user?.id;
          if (currentUserId) {
            const attributes = typeof this.state.message.attributes === 'string' 
              ? JSON.parse(this.state.message.attributes)
              : this.state.message.attributes;
            
            const aiFeedback = attributes?.ai_feedback;
            if (aiFeedback?.feedback_entries && Array.isArray(aiFeedback.feedback_entries)) {
              // Compare as numbers to handle type differences
              const currentUserIdNum = Number(currentUserId);
              hasUserThumbsUp = aiFeedback.feedback_entries.some(
                (entry) => entry.feedback_type === 'thumbs_up' && Number(entry.reviewed_by_id) === currentUserIdNum
              );
            }
          }
        } catch (error) {
          console.error('Error checking thumbs up feedback:', error);
        }
      }
      
      // Check suggested edit - use local state if set, otherwise check message attributes
      if (this.state.localSuggestedEdit !== null) {
        hasUserSuggestedEdit = this.state.localSuggestedEdit;
      } else {
        try {
          const currentUserId = JSON.parse(window.localStorage.getItem('bootstrap'))?.user?.id;
          if (currentUserId) {
            const attributes = typeof this.state.message.attributes === 'string' 
              ? JSON.parse(this.state.message.attributes)
              : this.state.message.attributes;
            
            const aiFeedback = attributes?.ai_feedback;
            if (aiFeedback?.feedback_entries && Array.isArray(aiFeedback.feedback_entries)) {
              const currentUserIdNum = Number(currentUserId);
              hasUserSuggestedEdit = aiFeedback.feedback_entries.some(
                (entry) => entry.feedback_type === 'suggested_edit' && Number(entry.reviewed_by_id) === currentUserIdNum
              );
            }
          }
        } catch (error) {
          console.error('Error checking suggested edit feedback:', error);
        }
      }
    }

    // If automated, include markdown (CP-6043)
    //if (!this.state.message.author.includes("_")) {
      body = simple_markdown_to_html(body, { allow_links })
    //}

    // CP-8122: inbound SMS/WhatsApp/Airbnb ingest appends each stored attachment's S3 URL to the
    // message text. When the media is also attached to the Twilio message (rendered inline below),
    // a body that is nothing but those URLs is pure duplication — hide it. Media URLs that do
    // remain visible (docs, multi-photo, photo+text, pre-fix messages) render as short labeled
    // links instead of ~200-char URLs.
    const hideBody = this.mediaGroups.length > 0 && is_all_media_links(this.state.message.body);
    body = shorten_attachment_links(body);

    if(source_platform && source_platform != ""){
      source_platform = "Sent via " + source_platform;
    }
    else{
      source_platform = "Sent via...";
    }

    if(exclude_delivery_to_agents){
      source_platform = source_platform + " | Excluded travel agencies and OTAs"
    }

    // CP-8179: a delivery blocked by a blacklist rule and emailed to the rule's redirect
    // inbox instead reaches the reseller/agency, never the guest. The badge itself says so
    // ("Sent via email re-directed" — the backend adds EMAIL RE-DIRECTED to send_methods).
    // The tooltip is deliberately generic: Twilio message attributes reach every
    // participant's client (guest app / web widget included), so the backend never puts
    // the redirect inbox addresses in them. Look the addresses up in the admin
    // (EmailMessage, blacklist redirect fields) when needed.
    const source_platform_tooltip = source_platform.includes("email re-directed")
      ? "A delivery blacklist rule blocked sending this to the guest; it was emailed to the rule's redirect inbox instead"
      : null;

    // The Twilio identity is shown while loading (while waiting for the author name to be retrieved).
    // Rather than show the identiy, show nothing.
    let author = ""; //this.state.message.author;
    let role = "";
    const role_bg_color_map = { 'Admin': cv('tag-teal', '#B7EBE3'), 'Tour Guide': cv('tag-lavender', '#E4E4F9'), 'Guest': cv('tag-pink', '#F8D7DA'), 'Observer': cv('tag-blue', 'lightblue'), '':'' };
    if (this.state.message.author in this.props.participantsDict){
      author = this.props.participantsDict[this.state.message.author];
    }
    if (this.state.message.author in this.props.participantsRoleDict){
      role = this.props.participantsRoleDict[this.state.message.author];
    }

    // CP-8243: classify the sender type driving the persistent tag chip and the
    // 2px left border accent on the bubble. "Chatbot" = conversational
    // auto-replies, whose AUTOMATED SYSTEM MESSAGE source_platform label always
    // carries a (…-REPLY) suffix — (AI AUTO-REPLY), (OOO AUTO-REPLY),
    // (GUIDE ABSENT AUTO-REPLY), (NOTIFICATION AUTO-REPLY), (NO-REPLY), etc.
    // A plain AUTOMATED SYSTEM MESSAGE label (no suffix) is a notification
    // relay, which — like anything with an AUTOMATED send_methods entry or a
    // bare no-underscore sender identity (SYSTEM / the operator name) — tags as
    // "Automated". The chatbot check runs first: auto-replies also gain an
    // AUTOMATED send_methods entry once delivery attributes are stamped. Own
    // messages and observer messages keep their pre-CP-8243 rendering.
    const source_platform_upper = source_platform_attr ? source_platform_attr.toUpperCase() : "";
    let messageType = null;
    if (!isOwnMessage) {
      if (isAIAutoReply
          || (source_platform_upper.includes("AUTOMATED SYSTEM MESSAGE") && source_platform_upper.includes("REPLY"))) {
        messageType = 'chatbot';
      }
      else if (!this.state.message.author?.includes("_")
          || (send_methods || []).join(",").toUpperCase().includes("AUTOMATED")
          || source_platform_upper.includes("AUTOMATED SYSTEM MESSAGE")) {
        messageType = 'automated';
      }
      else if (role === 'Admin') {
        messageType = 'team';
      }
      else if (role === 'Guest') {
        messageType = 'guest';
      }
      else if (role === 'Tour Guide') {
        messageType = 'guide';
      }
    }
    const typeTokens = messageType ? MESSAGE_TYPE_TOKENS[messageType] : null;
    const roleTagLabel = typeTokens ? typeTokens.label : role;
    const roleTagBackground = typeTokens ? typeTokens.tagBackground : role_bg_color_map[role];

    let avatarColor = "white";
    if ( !(this.state.message.author in this.props.participantsAvatarColorDict) ){
      //const l = Object.keys(this.props.participantsAvatarColorDict).length;
      //this.props.participantsAvatarColorDict[this.state.message.author]  = this.props.colorPalette[l];
    }
    else{
      avatarColor = this.props.participantsAvatarColorDict[this.state.message.author];
    }
    
    let avatarImg = null
    if ( this.state.message.author in this.props.participantsAvatarImgDict ){
      avatarImg = this.props.participantsAvatarImgDict[this.state.message.author];
    }
    // CP-8243: scheduled automation messages show the operator's logo, while
    // chatbot messages keep the bot symbol avatar
    if (messageType === 'automated' && this.props.operatorLogoUrl) {
      avatarImg = this.props.operatorLogoUrl;
    }
    return (
      <ListItem style={styles.listItem(isOwnMessage, isAIAutoReply)}  sx={{ '& a': { wordBreak: "break-all" }, }}  >
        {this.props.nextMsg?.author !== this.state.message.author || new Date(this.props.nextMsg?.dateCreated).toDateString() !== new Date(this.state.message.dateCreated).toDateString() ?
        <Avatar style={styles.avatar(isOwnMessage)} name={author} size="25" round={true} color={avatarColor} src={avatarImg} maxInitials={1} textSizeRatio={2} /> 
        : <div style={styles.avatar(isOwnMessage)} />
        }
        <span style={styles.textBox(isOwnMessage, this.props.prevMsg?.author === this.state.message.author && 
                                                  new Date(this.props.prevMsg?.dateCreated).toDateString() === new Date(this.state.message.dateCreated).toDateString(), isAIAutoReply)}>
          <div style={styles.messageWrapperContainer(isOwnMessage)}>
            <div style={styles.messageWrapper(isOwnMessage)} ref={(el) => { if (el && !isOwnMessage) this.messageWrapperRef = el; }}>
            <div style={styles.container(isOwnMessage, typeTokens)} data-own-message={isOwnMessage || undefined}>
            {(this.props.prevMsg?.author !== this.state.message.author || new Date(this.props.prevMsg?.dateCreated).toDateString() !== new Date(this.state.message.dateCreated).toDateString()) ?
            <div style={styles.author(isOwnMessage)}>
              {author}&nbsp;
              <span style={{ backgroundColor: roleTagBackground, padding: '2px 8px', borderRadius: '2px', whiteSpace: "nowrap" }}>{roleTagLabel}</span>
            </div>
            : ""}
            {!hideBody &&
            <span style={{float: isOwnMessage ? "right" : null, maxWidth: "100%"}} dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(linkify(ota_reply_links(body)), { ADD_ATTR: ['target'] })}}></span>}
            {this.mediaGroups.length > 0 &&
              <div ref={this.mediaContainerRef}>
                {this.mediaGroups.map((group, idx) => this.renderMediaGroup(group, idx))}
              </div>}
            {this.state.translationVisible && this.state.translation && (
              <div style={styles.translationBlock(isOwnMessage)}>
                <div style={styles.translationLabel(isOwnMessage)}>
                  {this.state.translationSourceLang
                    ? `Translated from ${this.state.translationSourceLang.toUpperCase()} → ${(this.state.translationLang || '').toUpperCase()}`
                    : `Translated → ${(this.state.translationLang || '').toUpperCase()}`}
                </div>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(linkify(ota_reply_links(simple_markdown_to_html(this.state.translation, { allow_links }))), { ADD_ATTR: ['target'] }) }} />
              </div>
            )}
            {this.state.translationError && (
              <div style={styles.translationError(isOwnMessage)}>{this.state.translationError}</div>
            )}
            <br/>
            <span style={styles.timestamp(isOwnMessage)}>
              {isOwnMessage && <span style={{ fontWeight: 600 }} title={source_platform_tooltip}>{source_platform}&nbsp;|&nbsp;</span>}
              {new Date(this.state.message.dateCreated.toISOString()).toLocaleString([], { timeStyle: 'short' })}
              {!isOwnMessage && <span style={{ fontWeight: 600 }} title={source_platform_tooltip}>&nbsp;|&nbsp;{source_platform}</span>}
              {!isOwnMessage && (
                <Tooltip
                  title={
                    this.state.translation
                      ? (this.state.translationVisible ? "Hide translation" : "Show translation")
                      : "Translate to my language"
                  }
                  arrow
                  placement="top"
                >
                <span
                  onClick={this.handleTranslateClick}
                  style={styles.inlineTranslateButton(this.state.translationVisible, this.state.translationLoading)}
                >
                  &nbsp;|&nbsp;
                  {this.state.translationLoading
                    ? <CircularProgress size={10} thickness={5} sx={{ verticalAlign: '-1px' }} />
                    : <TranslateIcon sx={{ fontSize: 11, verticalAlign: '-3px' }} />}
                </span>
                </Tooltip>
              )}
            </span>
            <Link onClick={e => this.props.toggleParticipantsList(e, this.state.message.index)} style={{ position: "absolute", zIndex: 10, top: 6, right: 0, color: cv('text-muted', '#8A8A8F'), cursor: 'pointer' }}>
              <NavigateNextIcon style={{ verticalAlign: 'middle', color: isOwnMessage ? 'white' : null }} />
            </Link>
            </div>
            </div>
            {isAIAutoReply && (
            <div style={styles.actionButtons(isOwnMessage)}>
              <Tooltip title="Mark as good response" arrow placement="top"><IconButton
                size="small"
                style={styles.actionButton}
                onClick={() => {
                  if (this.props.handleThumbsUpFeedback) {
                    // Set up callback to update local state after success
                    this.state.message.updateThumbsUpState = (value) => {
                      this.setState({ localThumbsUp: value });
                    };
                    this.props.handleThumbsUpFeedback(this.state.message);
                  }
                }}
              >
                <ThumbUpOutlinedIcon sx={{ fontSize: '14px', color: hasUserThumbsUp ? '#4CAF50' : 'inherit' }} />
              </IconButton></Tooltip>
              <Tooltip title="Suggest an edit" arrow placement="top"><IconButton
                size="small"
                style={styles.actionButton}
                onClick={() => {
                  if (this.props.openTrainResponsePanel) {
                    // Set up callback to update local state after success
                    this.state.message.updateSuggestedEditState = (value) => {
                      this.setState({ localSuggestedEdit: value });
                    };
                    this.props.openTrainResponsePanel(this.state.message, this.props.prevMsg);
                  }
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: '14px', color: hasUserSuggestedEdit ? '#4CAF50' : 'inherit' }} />
              </IconButton></Tooltip>
            </div>
            )}
          </div>
        </span>
      </ListItem>
    );
  }
}

const styles = {
  textBox: (isOwnMessage, prevMsgHasSameAuthor, isAIAutoReply) => ({
    display: "flex",
    width: "100%",
    paddingLeft: "6px",
    paddingRight: "16px",
    // CP-8243: 6px between consecutive messages from the same sender, 20px
    // when the sender changes
    paddingTop: prevMsgHasSameAuthor ? `${SAME_SENDER_GAP_PX}px` : `${DIFFERENT_SENDER_GAP_PX}px`,
    flexDirection: "column",
    alignItems: isOwnMessage ? "flex-end" : "flex-start",
    alignSelf: "flex-start",
    position: "relative",
    minHeight: 0,
  }),
  listItem: (isOwnMessage, isAIAutoReply) => ({
    flexDirection: "row",
    paddingTop: 0,
    paddingBottom: isAIAutoReply ? "32px" : 0,
    alignItems: "flex-start",
    position: "relative",
  }),
  avatar: (isOwnMessage) => ({
    width: "26px",
    minWidth: "26px",
    display: isOwnMessage ? "none" : "flex",
    alignSelf: "flex-end",
  }),
  messageWrapperContainer: (isOwnMessage) => ({
    position: 'relative',
    display: 'inline-block',
    maxWidth: '85%',
  }),
  messageWrapper: (isOwnMessage) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
    maxWidth: '100%',
    width: 'fit-content',
  }),
  container: (isOwnMessage, typeTokens) => ({
    borderRadius: 12,
    paddingTop: 6,
    paddingBottom: 4,
    paddingLeft: 14,
    paddingRight: 24,
    color: isOwnMessage ? "white" : cv('text', "black"),
    fontSize: 13,
    backgroundColor: isOwnMessage ? cv('accent-fill', "#007AFF") : (typeTokens?.bubbleBackground || cv('surface-muted', "#F5F5F5")),
    // CP-8243 sender-type accent; applies on every message of a same-sender
    // run, independent of read state or position in the thread
    borderLeft: !isOwnMessage && typeTokens ? `2px solid ${typeTokens.borderAccent}` : undefined,
    maxWidth: "100%",
    overflowWrap: "break-word",
    whiteSpace: "pre-wrap",
    position: "relative",
  }),
  imageDiv: { maxWidth: "250px", borderRadius: 3, boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)" },
  imageSkeleton: { width: 250, maxWidth: "100%", height: 160, borderRadius: "3px" },
  videoThumbnailContainer: { position: "relative", display: "inline-block", maxWidth: "250px" },
  playButtonOverlay: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none", opacity: 0.9 },
  author: (isOwnMessage) => ({ 
    display: isOwnMessage ? "none" : "block",
    fontSize: 13,
    fontWeight: 500,
    color: cv('text', "black") 
  }),
  timestamp: (isOwnMessage) => ({
    fontSize: 9,
    color: isOwnMessage ? "white" : cv('text-muted', "#8A8A8F"),
    textAlign: "right",
    paddingTop: 4,
    position: "relative",
    bottom: "-0.2em",
    //whiteSpace: "nowrap",
    float: isOwnMessage ? "right" : null
  }),
  fileIcon: { paddingLeft: "space20", paddingRight: "space50" },
  mediaLinkText: { fontWeight: 'bold', fontSize: '8px', textAlign: "center" },
  translationBlock: (isOwnMessage) => ({
    marginTop: 6,
    paddingTop: 6,
    borderTop: isOwnMessage ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${cv('border', 'rgba(0,0,0,0.10)')}`,
    fontSize: 13,
    fontStyle: "italic",
    color: isOwnMessage ? "rgba(255,255,255,0.92)" : cv('text', "#222"),
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
  }),
  translationLabel: (isOwnMessage) => ({
    fontSize: 10,
    fontStyle: "normal",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 2,
    color: isOwnMessage ? "rgba(255,255,255,0.75)" : cv('text-muted', "#8A8A8F"),
  }),
  translationError: (isOwnMessage) => ({
    marginTop: 6,
    fontSize: 11,
    fontStyle: "italic",
    color: isOwnMessage ? "#FFD2D2" : cv('danger-text', "#B00020"),
  }),
  inlineTranslateButton: (translationVisible, translationLoading) => ({
    cursor: translationLoading ? "default" : "pointer",
    color: translationVisible ? cv('accent', "#007AFF") : cv('text-muted', "#8A8A8F"),
    fontWeight: 600,
    userSelect: "none",
  }),
  actionButtons: (isOwnMessage) => ({
    display: 'flex',
    gap: '4px',
    marginTop: '4px',
    justifyContent: 'flex-end',
    width: '100%',
    position: 'absolute',
    top: 'calc(100% + 2px)',
    right: 0,
  }),
  actionButton: {
    padding: '2px',
    border: `1px solid ${cv('border', '#E0E0E0')}`,
    borderRadius: '6px',
    color: cv('text-secondary', '#666'),
    minWidth: '22px',
    width: '22px',
    height: '22px',
    '&:hover': {
      backgroundColor: cv('surface-muted', '#F5F5F5'),
    },
  },
  closeButton: {
    position: "absolute",
    right: "16px",
    top: "0",
    fontSize: "28px",
    lineHeight: "50px",
    cursor: "pointer",
    color: cv('text-secondary', "#666"),
    fontWeight: 300,
  },
};

export default ChatItem;
